import React, { useEffect, useState } from 'react';
import { getApiBaseUrl, getPythonApiUrl } from '../../utils/apiConfig';

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  services: {
    javaBackend: ServiceStatus;
    pythonService: ServiceStatus;
    database: ServiceStatus;
    exchange: ServiceStatus;
  };
  metrics: {
    totalBots: number;
    runningBots: number;
    totalTrades: number;
    systemUptime: string;
    memoryUsage: number;
    cpuUsage: number;
  };
  alerts: Alert[];
}

interface ServiceStatus {
  name: string;
  status: 'up' | 'down' | 'degraded';
  responseTime?: number;
  lastCheck: string;
  error?: string;
}

interface Alert {
  id: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  timestamp: string;
  resolved: boolean;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  category: 'system' | 'trading' | 'risk' | 'performance';
  actions?: string[];
}

interface PerformanceMetrics {
  responseTime: number;
  throughput: number;
  errorRate: number;
  uptime: number;
  memoryUsage: number;
  cpuUsage: number;
  diskUsage: number;
  networkLatency: number;
}

interface TradingMetrics {
  totalBots: number;
  runningBots: number;
  totalTrades: number;
  totalVolume: number;
  totalPnl: number;
  activeUsers: number;
  riskViolations: number;
  systemLoad: number;
}

const SystemHealthPanel: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [tradingMetrics, setTradingMetrics] = useState<TradingMetrics | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'alerts' | 'trading'>('overview');

  const fetchSystemHealth = async () => {
    try {
      setLoading(true);
      
      // Check Java backend health
      const apiUrl = getApiBaseUrl();
      const pythonApiUrl = getPythonApiUrl();
      
      const javaHealth = await checkServiceHealth(`${apiUrl}/actuator/health`);
      
      // Check Python service health
      const pythonHealth = await checkServiceHealth(`${pythonApiUrl}/health`);
      
      // Check exchange connectivity
      const exchangeHealth = await checkServiceHealth(`${pythonApiUrl}/exchange/health`);
      
      // Mock database health (in real implementation, this would check actual DB)
      const databaseHealth: ServiceStatus = {
        name: 'Database',
        status: 'up',
        responseTime: 15,
        lastCheck: new Date().toISOString()
      };

      // Calculate overall system status
      const services = [javaHealth, pythonHealth, exchangeHealth, databaseHealth];
      const overallStatus = services.every(s => s.status === 'up') ? 'healthy' :
                           services.some(s => s.status === 'down') ? 'critical' : 'warning';

      // Mock metrics (in real implementation, these would come from monitoring system)
      const metrics = {
        totalBots: 5,
        runningBots: 2,
        totalTrades: 1247,
        systemUptime: '7d 14h 23m',
        memoryUsage: 68.5,
        cpuUsage: 23.2
      };

      // Mock performance metrics
      const perfMetrics: PerformanceMetrics = {
        responseTime: 145,
        throughput: 1250,
        errorRate: 0.02,
        uptime: 99.8,
        memoryUsage: 68.5,
        cpuUsage: 23.2,
        diskUsage: 45.3,
        networkLatency: 12
      };

      // Mock trading metrics
      const tradeMetrics: TradingMetrics = {
        totalBots: 12,
        runningBots: 8,
        totalTrades: 1247,
        totalVolume: 1250000,
        totalPnl: 15750,
        activeUsers: 45,
        riskViolations: 3,
        systemLoad: 67
      };

      // Enhanced alerts
      const alerts: Alert[] = [
        {
          id: '1',
          type: 'warning',
          message: 'High memory usage detected on Python service',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          resolved: false,
          severity: 'medium',
          source: 'Python Service',
          category: 'system',
          actions: ['Restart service', 'Scale resources', 'Investigate memory leak']
        },
        {
          id: '2',
          type: 'error',
          message: 'Risk violation: Bot #3 exceeded daily drawdown limit',
          timestamp: new Date(Date.now() - 600000).toISOString(),
          resolved: true,
          severity: 'high',
          source: 'Risk Management',
          category: 'risk',
          actions: ['Stop bot', 'Review strategy', 'Adjust limits']
        },
        {
          id: '3',
          type: 'info',
          message: 'Exchange API latency increased to 250ms',
          timestamp: new Date(Date.now() - 900000).toISOString(),
          resolved: false,
          severity: 'low',
          source: 'Exchange API',
          category: 'performance',
          actions: ['Monitor', 'Switch to backup exchange']
        },
        {
          id: '4',
          type: 'warning',
          message: 'Unusual trading volume detected on BTC/USDT',
          timestamp: new Date(Date.now() - 1200000).toISOString(),
          resolved: false,
          severity: 'medium',
          source: 'Trading Engine',
          category: 'trading',
          actions: ['Review trades', 'Check market conditions', 'Adjust position sizes']
        }
      ];

      setHealth({
        status: overallStatus,
        services: {
          javaBackend: javaHealth,
          pythonService: pythonHealth,
          database: databaseHealth,
          exchange: exchangeHealth
        },
        metrics,
        alerts
      });

      setPerformanceMetrics(perfMetrics);
      setTradingMetrics(tradeMetrics);
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch system health:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkServiceHealth = async (url: string): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      const response = await fetch(url, { 
        method: 'GET',
        timeout: 5000 
      } as any);
      
      const responseTime = Date.now() - startTime;
      
      if (response.ok) {
        return {
          name: getServiceName(url),
          status: 'up',
          responseTime,
          lastCheck: new Date().toISOString()
        };
      } else {
        return {
          name: getServiceName(url),
          status: 'degraded',
          responseTime,
          lastCheck: new Date().toISOString(),
          error: `HTTP ${response.status}`
        };
      }
    } catch (error) {
      return {
        name: getServiceName(url),
        status: 'down',
        lastCheck: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const getServiceName = (url: string): string => {
    if (url.includes('8080')) return 'Java Backend';
    if (url.includes('8000')) return 'Python Service';
    if (url.includes('exchange')) return 'Exchange API';
    return 'Unknown Service';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'up': return '#28a745';
      case 'degraded': return '#ffc107';
      case 'down': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'up': return '✅';
      case 'degraded': return '⚠️';
      case 'down': return '❌';
      default: return '❓';
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  useEffect(() => {
    fetchSystemHealth();
    const interval = setInterval(fetchSystemHealth, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  if (loading && !health) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <div>Loading system health...</div>
      </div>
    );
  }

  if (!health) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#dc3545' }}>
        <div>Failed to load system health data</div>
        <button onClick={fetchSystemHealth} style={{ marginTop: 10 }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-content-header">
        <h2 className="content-title">System Health</h2>
        <div className="content-breadcrumb">Admin Dashboard / System Health</div>
      </div>

      <div className="admin-content-body">
        {/* Overall Status */}
        <div style={{ 
          background: '#fff', 
          border: '1px solid #dee2e6', 
          borderRadius: 8, 
          padding: 20, 
          marginBottom: 24,
          borderLeft: `4px solid ${getStatusColor(health.status)}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, color: getStatusColor(health.status) }}>
                System Status: {health.status.toUpperCase()}
              </h3>
              <p style={{ margin: '8px 0 0 0', color: '#6c757d' }}>
                Last updated: {lastUpdate.toLocaleTimeString()}
              </p>
            </div>
            <button onClick={fetchSystemHealth} disabled={loading}>
              {loading ? 'Updating...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          borderBottom: `2px solid #dee2e6`,
          marginBottom: 24
        }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'performance', label: 'Performance' },
            { id: 'trading', label: 'Trading Metrics' },
            { id: 'alerts', label: 'Alerts & Monitoring' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === tab.id ? '#007bff' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#495057',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? `2px solid #007bff` : '2px solid transparent',
                fontWeight: activeTab === tab.id ? 600 : 400
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Services Status */}
            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
              <h3 style={{ marginTop: 0 }}>Services Status</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                {Object.entries(health.services).map(([key, service]) => (
                  <div key={key} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: 12,
                    background: '#f8f9fa',
                    borderRadius: 6,
                    border: `1px solid ${getStatusColor(service.status)}20`
                  }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {getStatusIcon(service.status)} {service.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#6c757d' }}>
                        {service.responseTime ? `${service.responseTime}ms` : 'N/A'} • 
                        {new Date(service.lastCheck).toLocaleTimeString()}
                      </div>
                      {service.error && (
                        <div style={{ fontSize: 12, color: '#dc3545' }}>
                          {service.error}
                        </div>
                      )}
                    </div>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      fontSize: 12,
                      backgroundColor: `${getStatusColor(service.status)}20`,
                      color: getStatusColor(service.status)
                    }}>
                      {service.status.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* System Metrics */}
            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
              <h3 style={{ marginTop: 0 }}>System Metrics</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Total Bots:</span>
                  <span style={{ fontWeight: 600 }}>{health.metrics.totalBots}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Running Bots:</span>
                  <span style={{ fontWeight: 600, color: '#28a745' }}>{health.metrics.runningBots}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Total Trades:</span>
                  <span style={{ fontWeight: 600 }}>{health.metrics.totalTrades.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>System Uptime:</span>
                  <span style={{ fontWeight: 600 }}>{health.metrics.systemUptime}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Memory Usage:</span>
                  <span style={{ fontWeight: 600, color: health.metrics.memoryUsage > 80 ? '#dc3545' : '#28a745' }}>
                    {health.metrics.memoryUsage}%
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>CPU Usage:</span>
                  <span style={{ fontWeight: 600, color: health.metrics.cpuUsage > 80 ? '#dc3545' : '#28a745' }}>
                    {health.metrics.cpuUsage}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && performanceMetrics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
              <h3 style={{ marginTop: 0 }}>Performance Metrics</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Response Time:</span>
                  <span style={{ fontWeight: 600, color: performanceMetrics.responseTime > 200 ? '#dc3545' : '#28a745' }}>
                    {performanceMetrics.responseTime}ms
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Throughput:</span>
                  <span style={{ fontWeight: 600 }}>{performanceMetrics.throughput.toLocaleString()} req/min</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Error Rate:</span>
                  <span style={{ fontWeight: 600, color: performanceMetrics.errorRate > 0.05 ? '#dc3545' : '#28a745' }}>
                    {(performanceMetrics.errorRate * 100).toFixed(2)}%
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Uptime:</span>
                  <span style={{ fontWeight: 600, color: performanceMetrics.uptime > 99 ? '#28a745' : '#dc3545' }}>
                    {performanceMetrics.uptime}%
                  </span>
                </div>
              </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
              <h3 style={{ marginTop: 0 }}>Resource Usage</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Memory Usage:</span>
                  <span style={{ fontWeight: 600, color: performanceMetrics.memoryUsage > 80 ? '#dc3545' : '#28a745' }}>
                    {performanceMetrics.memoryUsage}%
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>CPU Usage:</span>
                  <span style={{ fontWeight: 600, color: performanceMetrics.cpuUsage > 80 ? '#dc3545' : '#28a745' }}>
                    {performanceMetrics.cpuUsage}%
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Disk Usage:</span>
                  <span style={{ fontWeight: 600, color: performanceMetrics.diskUsage > 80 ? '#dc3545' : '#28a745' }}>
                    {performanceMetrics.diskUsage}%
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Network Latency:</span>
                  <span style={{ fontWeight: 600, color: performanceMetrics.networkLatency > 100 ? '#dc3545' : '#28a745' }}>
                    {performanceMetrics.networkLatency}ms
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'trading' && tradingMetrics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
              <h3 style={{ marginTop: 0 }}>Trading Activity</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Total Bots:</span>
                  <span style={{ fontWeight: 600 }}>{tradingMetrics.totalBots}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Running Bots:</span>
                  <span style={{ fontWeight: 600, color: '#28a745' }}>{tradingMetrics.runningBots}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Total Trades:</span>
                  <span style={{ fontWeight: 600 }}>{tradingMetrics.totalTrades.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Total Volume:</span>
                  <span style={{ fontWeight: 600 }}>${tradingMetrics.totalVolume.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
              <h3 style={{ marginTop: 0 }}>Performance & Risk</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Total P&L:</span>
                  <span style={{ fontWeight: 600, color: tradingMetrics.totalPnl >= 0 ? '#28a745' : '#dc3545' }}>
                    ${tradingMetrics.totalPnl.toLocaleString()}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Active Users:</span>
                  <span style={{ fontWeight: 600 }}>{tradingMetrics.activeUsers}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>Risk Violations:</span>
                  <span style={{ fontWeight: 600, color: tradingMetrics.riskViolations > 0 ? '#dc3545' : '#28a745' }}>
                    {tradingMetrics.riskViolations}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                  <span>System Load:</span>
                  <span style={{ fontWeight: 600, color: tradingMetrics.systemLoad > 80 ? '#dc3545' : '#28a745' }}>
                    {tradingMetrics.systemLoad}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Alerts & Monitoring</h3>
            {health.alerts.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#6c757d' }}>
                No recent alerts
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {health.alerts.map(alert => (
                  <div key={alert.id} style={{ 
                    padding: 16,
                    background: alert.resolved ? '#f8f9fa' : `${getAlertColor(alert.type)}10`,
                    borderRadius: 8,
                    border: `1px solid ${getAlertColor(alert.type)}30`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ 
                            padding: '4px 8px', 
                            borderRadius: 4, 
                            fontSize: 12,
                            backgroundColor: getAlertColor(alert.type),
                            color: 'white',
                            marginRight: 12
                          }}>
                            {alert.severity.toUpperCase()}
                          </span>
                          <span style={{ 
                            padding: '4px 8px', 
                            borderRadius: 4, 
                            fontSize: 12,
                            backgroundColor: '#6c757d',
                            color: 'white'
                          }}>
                            {alert.category.toUpperCase()}
                          </span>
                        </div>
                        <div style={{ fontWeight: 600, color: getAlertColor(alert.type), marginBottom: 4 }}>
                          {alert.message}
                        </div>
                        <div style={{ fontSize: 14, color: '#6c757d', marginBottom: 8 }}>
                          Source: {alert.source} • {new Date(alert.timestamp).toLocaleString()}
                        </div>
                        {alert.actions && alert.actions.length > 0 && (
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Recommended Actions:</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {alert.actions.map((action, index) => (
                                <button
                                  key={index}
                                  style={{
                                    padding: '4px 12px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 4,
                                    fontSize: 12,
                                    cursor: 'pointer'
                                  }}
                                >
                                  {action}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <span style={{ 
                        padding: '6px 12px', 
                        borderRadius: 4, 
                        fontSize: 12,
                        backgroundColor: alert.resolved ? '#28a745' : getAlertColor(alert.type),
                        color: 'white',
                        fontWeight: 600
                      }}>
                        {alert.resolved ? 'RESOLVED' : 'ACTIVE'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemHealthPanel;
